import { computed } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';

import {
  clonePaymentListFilters,
  CommissionPaymentListFilters,
  defaultPaymentListFilters,
  PAYMENT_METHOD_LABELS,
  PAYMENT_TYPE_LABELS,
} from '../../../../types/commission-payment.types';
import { OrganizationDTO } from '../../../../types/organization.types';

export interface CommissionPaymentFilterDisplayPill {
  key: string;
  label: string;
  type:
    | 'paymentType'
    | 'paymentMethod'
    | 'payeeOrganization'
    | 'payerOrganization';
}

interface CommissionPaymentFilterDisplayState {
  isDialogOpen: boolean;
  draftFilters: CommissionPaymentListFilters;
  appliedFilters: CommissionPaymentListFilters;
}

const initialState: CommissionPaymentFilterDisplayState = {
  isDialogOpen: false,
  draftFilters: defaultPaymentListFilters(),
  appliedFilters: defaultPaymentListFilters(),
};

export const CommissionPaymentFilterDisplayStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasActiveFilters: computed(() => {
      const filters = store.appliedFilters();
      return (
        filters.paymentType !== 'all' ||
        filters.paymentMethod !== 'all' ||
        !!filters.payeeOrganization ||
        !!filters.payerOrganization
      );
    }),
    activeFilterPills: computed<CommissionPaymentFilterDisplayPill[]>(() => {
      const filters = store.appliedFilters();
      return [
        ...(filters.paymentType !== 'all'
          ? [
              {
                key: 'paymentType',
                label: `Type: ${
                  PAYMENT_TYPE_LABELS[filters.paymentType] ??
                  filters.paymentType
                }`,
                type: 'paymentType' as const,
              },
            ]
          : []),
        ...(filters.paymentMethod !== 'all'
          ? [
              {
                key: 'paymentMethod',
                label: `Method: ${
                  PAYMENT_METHOD_LABELS[filters.paymentMethod] ??
                  filters.paymentMethod
                }`,
                type: 'paymentMethod' as const,
              },
            ]
          : []),
        ...(filters.payeeOrganization
          ? [
              {
                key: 'payeeOrganization',
                label: `Payee Org: ${
                  filters.payeeOrganization.name ?? filters.payeeOrganization.id
                }`,
                type: 'payeeOrganization' as const,
              },
            ]
          : []),
        ...(filters.payerOrganization
          ? [
              {
                key: 'payerOrganization',
                label: `Payer Org: ${
                  filters.payerOrganization.name ?? filters.payerOrganization.id
                }`,
                type: 'payerOrganization' as const,
              },
            ]
          : []),
      ];
    }),
  })),
  withMethods((store) => ({
    openDialog(appliedFilters: CommissionPaymentListFilters): void {
      const nextFilters = clonePaymentListFilters(appliedFilters);
      patchState(store, {
        isDialogOpen: true,
        appliedFilters: nextFilters,
        draftFilters: clonePaymentListFilters(nextFilters),
      });
    },

    closeDialog(): void {
      patchState(store, {
        isDialogOpen: false,
        draftFilters: clonePaymentListFilters(store.appliedFilters()),
      });
    },

    resetDraft(): void {
      patchState(store, { draftFilters: defaultPaymentListFilters() });
    },

    syncAppliedFilters(filters: CommissionPaymentListFilters): void {
      const nextFilters = clonePaymentListFilters(filters);
      patchState(store, {
        appliedFilters: nextFilters,
        draftFilters: clonePaymentListFilters(nextFilters),
      });
    },

    setPaymentType(value: CommissionPaymentListFilters['paymentType']): void {
      patchState(store, {
        draftFilters: {
          ...store.draftFilters(),
          paymentType: value ?? 'all',
        },
      });
    },

    setPaymentMethod(
      value: CommissionPaymentListFilters['paymentMethod']
    ): void {
      patchState(store, {
        draftFilters: {
          ...store.draftFilters(),
          paymentMethod: value ?? 'all',
        },
      });
    },

    setPayeeOrganization(organization: OrganizationDTO | null): void {
      patchState(store, {
        draftFilters: {
          ...store.draftFilters(),
          payeeOrganization: organization,
        },
      });
    },

    setPayerOrganization(organization: OrganizationDTO | null): void {
      patchState(store, {
        draftFilters: {
          ...store.draftFilters(),
          payerOrganization: organization,
        },
      });
    },

    applyDraft(): CommissionPaymentListFilters {
      const nextFilters = clonePaymentListFilters(store.draftFilters());
      patchState(store, {
        isDialogOpen: false,
        appliedFilters: nextFilters,
        draftFilters: clonePaymentListFilters(nextFilters),
      });
      return nextFilters;
    },

    clearAppliedFilter(
      pill: CommissionPaymentFilterDisplayPill
    ): CommissionPaymentListFilters {
      const filters = clonePaymentListFilters(store.appliedFilters());
      if (pill.type === 'paymentType') filters.paymentType = 'all';
      if (pill.type === 'paymentMethod') filters.paymentMethod = 'all';
      if (pill.type === 'payeeOrganization') filters.payeeOrganization = null;
      if (pill.type === 'payerOrganization') filters.payerOrganization = null;
      this.syncAppliedFilters(filters);
      return filters;
    },

    clearAllAppliedFilters(): CommissionPaymentListFilters {
      const filters = defaultPaymentListFilters();
      this.syncAppliedFilters(filters);
      return filters;
    },
  }))
);
